import { yupResolver } from '@hookform/resolvers/yup';
import { OtherIncomeModel, useUpdateOtherIncome } from 'api-hooks/other-income';
import formSetErrors from 'common/helpers/form-setError';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import Button from 'components/elements/buttons/button';
import useApprovalAction, { isDataDraft } from 'hooks/use-approval-action';
import useDismiss from 'hooks/use-dismiss';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import { isDataActive } from 'hooks/use-void-action';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  OtherIncomeUpdateFormSchema,
  OtherIncomeUpdateFormType,
} from './components/form-type';
import OtherIncomeCard from './components/other-income-card';

interface OtherIncomeViewProps {
  otherIncome: OtherIncomeModel;
  onClose: () => void;
}

export default function OtherIncomeView(props: OtherIncomeViewProps) {
  const { otherIncome, onClose } = props;
  const { t } = useTranslation();
  const isActive = isDataActive(otherIncome.status);
  const { mutateAsync } = useUpdateOtherIncome();
  const { onRequestApproval, requestApprovalButton } = useApprovalAction(
    'other-incomes',
    otherIncome,
  );

  const onDismiss = useDismiss('other-incomes');
  const defaultValues = React.useMemo<OtherIncomeUpdateFormType>(() => {
    return {
      accountableId: otherIncome.accountable?.id || null,
      accountType: otherIncome.accountType || null,
      acknowledgedAt: otherIncome.acknowledgedAt || null,
      amount: otherIncome.amount,
      description: otherIncome.description || '',
      transactionAt: otherIncome.transactionAt,
    };
  }, [
    otherIncome.accountType,
    otherIncome.accountable?.id,
    otherIncome.acknowledgedAt,
    otherIncome.amount,
    otherIncome.description,
    otherIncome.transactionAt,
  ]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(OtherIncomeUpdateFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: OtherIncomeUpdateFormType) => {
      try {
        const result = await mutateAsync({
          otherIncomeRefId: otherIncome.refId,
          body: values,
        });
        notification.success({
          message: result.message,
        });
        if (isDataDraft(result.data.status)) {
          onRequestApproval(result.data.id);
        } else {
          onDismiss(otherIncome.id);
          onDismiss(result.data.id);
        }
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        e.message &&
          notification.error({
            message: e.message,
          });
      }
    },
    [
      methods.setError,
      mutateAsync,
      onDismiss,
      onRequestApproval,
      otherIncome.id,
      otherIncome.refId,
      props,
    ],
  );
  const { revertDraftButton } = useRevertDraftAction(
    'other-incomes',
    otherIncome,
  );
  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t(`common:edit_extra`, {
          extra: t('common:other_incomes'),
        })}
      />

      <DrawerBody
        style={{
          marginTop: 0,
        }}
      >
        <Form
          methods={methods}
          onSubmit={onSubmit}
          keyName={`${otherIncome.id}-otherIncome`}
          noReset
        >
          <OtherIncomeCard type="view" />
          <Separator gap={16} />
          <FullContainer
            style={{ display: 'flex', justifyContent: 'end', gap: 8 }}
          >
            {isActive ? (
              <>
                <Button buttonType="tertiary" onClick={props.onClose}>
                  {t('common:cancel')}
                </Button>
                <Input type="submit" />
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                {revertDraftButton}
                {requestApprovalButton}
              </div>
            )}
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}
